import { useEffect, useRef, useState } from 'react';

import { RetailerNetworkLocationFragment } from '@occc/graphql';

import { PinCoords, fitBoundsToPins, resolveLocationsToCoords } from './store-map-section.utils';

type UseStoreMapPinsOptions = {
  isLoaded: boolean;
  boundsLocations: RetailerNetworkLocationFragment[];
  pinLocations: RetailerNetworkLocationFragment[];
  singlePinZoom: number;
};

type UseStoreMapPinsResult = {
  pins: PinCoords[];
  initialLoadComplete: boolean;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  onMapLoad: (map: google.maps.Map) => void;
};

export const useStoreMapPins = ({
  isLoaded,
  boundsLocations,
  pinLocations,
  singlePinZoom,
}: UseStoreMapPinsOptions): UseStoreMapPinsResult => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const boundsRef = useRef<PinCoords[]>([]);
  const [pins, setPins] = useState<PinCoords[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    if (!isLoaded) return;
    if (boundsLocations.length === 0) {
      // No results to bound to (e.g. filters/search matched nothing) — treat as loaded so the
      // map still renders, centered on the fallback, instead of staying hidden forever.
      boundsRef.current = [];
      setInitialLoadComplete(true);
      return;
    }
    let cancelled = false;
    const geocoder = new google.maps.Geocoder();
    resolveLocationsToCoords(boundsLocations, geocoder).then((resolved) => {
      if (cancelled) return;
      boundsRef.current = resolved;
      setInitialLoadComplete(true);
      if (mapRef.current) fitBoundsToPins(mapRef.current, resolved, singlePinZoom);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoaded, boundsLocations, singlePinZoom]);

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    if (pinLocations.length === 0) {
      setPins([]);
      return;
    }
    const geocoder = new google.maps.Geocoder();
    resolveLocationsToCoords(pinLocations, geocoder).then((resolved) => {
      if (cancelled) return;
      setPins([...resolved]);
    });
    return () => {
      cancelled = true;
    };
  }, [isLoaded, pinLocations]);

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (boundsRef.current.length > 0) {
      fitBoundsToPins(map, boundsRef.current, singlePinZoom);
    }
  };

  return { pins, initialLoadComplete, mapRef, onMapLoad };
};
